import {
  ChevronLeftIcon,
  ChevronRightIcon,
  MoreHorizontalIcon,
} from "lucide-react";
import { ComponentProps, FC } from "react";

import { Button } from "@/components/ui/button";
import { variants } from "@/components/ui/button/variants";
import { cn } from "@/lib/utils";

type PaginationProps = ComponentProps<"nav">;
type PaginationContentProps = ComponentProps<"ul">;
type PaginationItemProps = ComponentProps<"li">;

// TODO: Remove dependency of Button component
type PaginationLinkProps = Pick<ComponentProps<typeof Button>, "size"> &
  ComponentProps<"a"> & {
    isActive?: boolean;
  };
type PaginationPreviousProps = PaginationLinkProps;
type PaginationNextProps = PaginationLinkProps;
type PaginationEllipsisProps = ComponentProps<"span">;

export const Pagination: FC<PaginationProps> = ({ className, ...props }) => (
  <nav
    role="navigation"
    aria-label="pagination"
    data-slot="pagination"
    className={cn("mx-auto flex w-full justify-center", className)}
    {...props}
  />
);

export const PaginationContent: FC<PaginationContentProps> = ({
  className,
  ...props
}) => (
  <ul
    data-slot="pagination-content"
    className={cn("flex flex-row items-center gap-1", className)}
    {...props}
  />
);

export const PaginationItem: FC<PaginationItemProps> = (props) => (
  <li data-slot="pagination-item" {...props} />
);

export const PaginationLink: FC<PaginationLinkProps> = ({
  className,
  isActive,
  size = "icon",
  ...props
}) => (
  <a
    aria-current={isActive ? "page" : undefined}
    data-slot="pagination-link"
    data-active={isActive}
    className={cn(
      variants({
        variant: isActive ? "outline" : "ghost",
        size,
      }),
      className,
    )}
    {...props}
  />
);

export const PaginationPrevious: FC<PaginationPreviousProps> = ({
  className,
  ...props
}) => (
  <PaginationLink
    aria-label="Go to previous page"
    size="default"
    className={cn("gap-1 px-2.5 sm:pl-2.5", className)}
    {...props}
  >
    <ChevronLeftIcon />
    <span className="hidden sm:block">Previous</span>
  </PaginationLink>
);

export const PaginationNext: FC<PaginationNextProps> = ({
  className,
  ...props
}) => (
  <PaginationLink
    aria-label="Go to next page"
    size="default"
    className={cn("gap-1 px-2.5 sm:pr-2.5", className)}
    {...props}
  >
    <span className="hidden sm:block">Next</span>
    <ChevronRightIcon />
  </PaginationLink>
);

export const PaginationEllipsis: FC<PaginationEllipsisProps> = ({
  className,
  ...props
}) => (
  <span
    aria-hidden
    data-slot="pagination-ellipsis"
    className={cn("flex size-9 items-center justify-center", className)}
    {...props}
  >
    <MoreHorizontalIcon className="size-4" />
    <span className="sr-only">More pages</span>
  </span>
);
